import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './drawing-editor.scss';
import { TEXT_COLORS, compositeStrokes, drawStroke, useImageDisplayArea } from './utils';

const BRUSH_SIZES = [5, 10, 18];

const DrawingEditor = ({ file, imgSrc, onEditComplete, onRegisterApply, initialStrokes, onStrokesChange }) => {
    const containerRef = useRef(null);
    const mediaElementRef = useRef(null);
    const canvasRef = useRef(null);
    const applyFnRef = useRef(null);
    const strokesRef = useRef([...(initialStrokes ?? [])]);
    const currentStroke = useRef(null);

    const [drawColor, setDrawColor] = useState('#ffffff');
    const [brushSize, setBrushSize] = useState(10);
    const [canUndo, setCanUndo] = useState((initialStrokes?.length ?? 0) > 0);

    const { displayArea, onMediaLoad } = useImageDisplayArea(containerRef, mediaElementRef);

    const redrawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (strokesRef.current.length) compositeStrokes(canvas, strokesRef.current);
        if (currentStroke.current) {
            const s = currentStroke.current;
            drawStroke(ctx, s.points, s.color, s.size);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !displayArea) return;
        canvas.width = displayArea.width;
        canvas.height = displayArea.height;
        redrawCanvas();
    }, [displayArea]); // eslint-disable-line react-hooks/exhaustive-deps

    applyFnRef.current = () =>
        new Promise(resolve => {
            if (!imgSrc || !displayArea) {
                resolve(file);
                return;
            }

            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
                const outputCanvas = document.createElement('canvas');
                outputCanvas.width = img.naturalWidth;
                outputCanvas.height = img.naturalHeight;
                const ctx = outputCanvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                if (strokesRef.current.length) {
                    const scaleX = img.naturalWidth / displayArea.width;
                    const scaleY = img.naturalHeight / displayArea.height;
                    const scaled = strokesRef.current.map(s => ({
                        ...s,
                        points: s.points.map(p => ({ x: p.x * scaleX, y: p.y * scaleY })),
                        size: s.size * Math.min(scaleX, scaleY),
                    }));
                    compositeStrokes(outputCanvas, scaled);
                }

                outputCanvas.toBlob(
                    blob => {
                        if (!blob) {
                            resolve(file);
                            return;
                        }
                        const editedFile = new File([blob], file.name, {
                            type: file.type || 'image/jpeg',
                            lastModified: Date.now(),
                        });
                        onEditComplete?.(editedFile);
                        resolve(editedFile);
                    },
                    'image/jpeg',
                    0.92,
                );
            };
        });

    useEffect(() => {
        onRegisterApply?.(async () => await applyFnRef.current?.());
        return () => onRegisterApply?.(null);
    }, [onRegisterApply]);

    const getPoint = e => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const src = e.touches ? e.touches[0] : e;
        return { x: src.clientX - rect.left, y: src.clientY - rect.top };
    };

    const onStart = e => {
        e.preventDefault();
        const pt = getPoint(e);
        currentStroke.current = { type: 'pen', color: drawColor, size: brushSize, points: [pt] };
    };

    const onMove = e => {
        if (!currentStroke.current) return;
        e.preventDefault();
        currentStroke.current.points.push(getPoint(e));
        redrawCanvas();
    };

    const onEnd = () => {
        if (!currentStroke.current) return;
        strokesRef.current.push(currentStroke.current);
        currentStroke.current = null;
        setCanUndo(true);
        onStrokesChange?.(strokesRef.current);
        redrawCanvas();
    };

    const undo = () => {
        strokesRef.current.pop();
        setCanUndo(strokesRef.current.length > 0);
        onStrokesChange?.(strokesRef.current);
        redrawCanvas();
    };

    return (
        <div className="se-draw-editor">
            <div className="se-draw-wrapper" ref={containerRef}>
                <img
                    ref={mediaElementRef}
                    src={imgSrc}
                    alt=""
                    className="se-draw-bg"
                    draggable={false}
                    onLoad={onMediaLoad}
                />

                {displayArea && (
                    <canvas
                        ref={canvasRef}
                        className="se-draw-canvas"
                        style={{
                            left: displayArea.left,
                            top: displayArea.top,
                            width: displayArea.width,
                            height: displayArea.height,
                        }}
                        onMouseDown={onStart}
                        onMouseMove={onMove}
                        onMouseUp={onEnd}
                        onMouseLeave={onEnd}
                        onTouchStart={onStart}
                        onTouchMove={onMove}
                        onTouchEnd={onEnd}
                    />
                )}

                {canUndo && (
                    <button type="button" className="wa-draw-undo" onClick={undo} title="Undo">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M12.5 8c-2.65 0-5.05 1-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
                        </svg>
                    </button>
                )}
            </div>

            <div className="se-draw-bottom">
                <div className="se-draw-sizes">
                    {BRUSH_SIZES.map(s => (
                        <button
                            key={s}
                            type="button"
                            className={`se-draw-size-btn${brushSize === s ? ' active' : ''}`}
                            onClick={() => setBrushSize(s)}
                            aria-label={`Brush size ${s}`}
                        >
                            <span
                                className="se-draw-size-dot"
                                style={{ width: s * 1.6, height: s * 1.6, background: drawColor }}
                            />
                        </button>
                    ))}
                </div>

                <div className="se-draw-palette">
                    {TEXT_COLORS.map(c => (
                        <button
                            key={c}
                            type="button"
                            className={`se-draw-color-dot${drawColor === c ? ' active' : ''}`}
                            style={{ background: c }}
                            onClick={() => setDrawColor(c)}
                            aria-label={c}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

DrawingEditor.propTypes = {
    file: PropTypes.object.isRequired,
    imgSrc: PropTypes.string.isRequired,
    onEditComplete: PropTypes.func,
    onRegisterApply: PropTypes.func,
    initialStrokes: PropTypes.array,
    onStrokesChange: PropTypes.func,
};

export default DrawingEditor;
